import http from '@/api/http';
import { ToastService } from '@/services/toast.service';
import {
  DashboardOverview,
  RecentActivity,
  ImportSummary,
  DashboardCharts,
  UpcomingArrival,
  ExpiringOffer
} from '../interfaces/dashboard.interface';
import {
  CompletedContainer,
  PortDepartures,
  PortTransitTime,
  CurrentPrice,
} from '../interfaces/mock.interface';

// Extended interface for latest offer from backend
export interface LatestOfferResponse {
  id: number;
  type: string;
  status: string;
  clientName: string;
  terminal: string;
  validityStartDate: string;
  validityEndDate: string;
  offerElements: {
    id: number;
    containerType: string;
    currency: string;
    price: number;
    validFrom: string;
    validTo: string;
  }[];
}

export class DashboardApi {
  private basePath = '/dashboard';

  // ==================== EXISTING METHODS ====================

  async getOverview(): Promise<DashboardOverview> {
    try {
      const response = await http.get<any>(`${this.basePath}/overview`);
      return response.data.data;
    } catch (error: any) {
      ToastService.error('Eroare la încărcarea datelor dashboard');
      throw error;
    }
  }

  async getRecentActivities(): Promise<RecentActivity[]> {
    try {
      const response = await http.get<any>(`${this.basePath}/recent-activities`);
      return response.data.data;
    } catch (error: any) {
      ToastService.error('Eroare la încărcarea activităților recente');
      throw error;
    }
  }

  async getImportSummary(): Promise<ImportSummary> {
    try {
      const response = await http.get<any>(`${this.basePath}/import-summary`);
      return response.data.data;
    } catch (error: any) {
      ToastService.error('Eroare la încărcarea sumarului de import');
      throw error;
    }
  }

  async getCharts(): Promise<DashboardCharts> {
    try {
      const response = await http.get<any>(`${this.basePath}/charts`);
      return response.data.data;
    } catch (error: any) {
      ToastService.error('Eroare la încărcarea datelor pentru grafice');
      throw error;
    }
  }

  async getUpcomingArrivals(): Promise<UpcomingArrival[]> {
    try {
      const response = await http.get<any>(`${this.basePath}/upcoming-arrivals`);
      return response.data.data;
    } catch (error: any) {
      ToastService.error('Eroare la încărcarea sosirilor apropiate');
      throw error;
    }
  }

  async getExpiringOffers(): Promise<ExpiringOffer[]> {
    try {
      const response = await http.get<any>(`${this.basePath}/expiring-offers`);
      return response.data.data;
    } catch (error: any) {
      ToastService.error('Eroare la încărcarea ofertelor care expiră');
      throw error;
    }
  }

  // ==================== NEW WIDGET METHODS ====================

  /**
   * Obține containerele finalizate în ultimele 30 zile
   */
  async getCompletedContainers(): Promise<CompletedContainer[]> {
    try {
      const response = await http.get<any>(`${this.basePath}/completed-containers`);
      return response.data.data;
    } catch (error: any) {
      ToastService.error('Nu s-au putut încărca containerele finalizate. Vă rugăm încercați din nou.');
      return [];
    }
  }

  /**
   * Obține plecările navelor din porturile principale
   */
  async getVesselDepartures(): Promise<PortDepartures[]> {
    try {
      const response = await http.get<any>(`${this.basePath}/vessel-departures`);
      return response.data.data;
    } catch (error: any) {
      ToastService.error('Nu s-au putut încărca plecările navelor. Vă rugăm încercați din nou.');
      return [];
    }
  }

  /**
   * Obține timpii medii de tranzit pe rute
   */
  async getPortTransitTimes(): Promise<PortTransitTime[]> {
    try {
      const response = await http.get<any>(`${this.basePath}/port-transit-times`);
      return response.data.data;
    } catch (error: any) {
      ToastService.error('Nu s-au putut încărca timpii de tranzit. Vă rugăm încercați din nou.');
      return [];
    }
  }

  /**
   * Obține prețurile curente ale navlului
   */
  async getCurrentPrices(): Promise<CurrentPrice[]> {
    try {
      const response = await http.get<any>(`${this.basePath}/current-prices`);
      return response.data.data;
    } catch (error: any) {
      ToastService.error('Nu s-au putut încărca prețurile curente. Vă rugăm încercați din nou.');
      return [];
    }
  }

  /**
   * Obține cea mai recentă ofertă activă
   */
  async getLatestOffer(): Promise<LatestOfferResponse | null> {
    try {
      const response = await http.get<any>(`${this.basePath}/latest-offer`);
      return response.data.data;
    } catch (error: any) {
      ToastService.error('Nu s-a putut încărca ultima ofertă. Vă rugăm încercați din nou.');
      return null;
    }
  }

  /**
   * Obține toate datele dashboard într-un singur apel
   * Util pentru încărcarea inițială și refresh
   */
  async getAllDashboardData(): Promise<{
    completedContainers: CompletedContainer[];
    vesselDepartures: PortDepartures[];
    portTransitTimes: PortTransitTime[];
    currentPrices: CurrentPrice[];
    latestOffer: LatestOfferResponse | null;
  }> {
    const [
      completedContainers,
      vesselDepartures,
      portTransitTimes,
      currentPrices,
      latestOffer,
    ] = await Promise.all([
      this.getCompletedContainers(),
      this.getVesselDepartures(),
      this.getPortTransitTimes(),
      this.getCurrentPrices(),
      this.getLatestOffer(),
    ]);

    return {
      completedContainers,
      vesselDepartures,
      portTransitTimes,
      currentPrices,
      latestOffer,
    };
  }
}

export const dashboardApi = new DashboardApi();
